import {useSelector} from 'react-redux';

import {canUseMsgPhoneOrLatInbox} from 'src/selectors/chat';
import usePrivilege from 'src/hooks/usePrivilege';
import useAgencyConfig, {
  useSchedulerEnabled,
  useDisableNewMessageCompose,
} from 'src/hooks/useAgencyConfig';
import {
  useAppShellEnabled,
  useReleaseFlag,
  useCandidateDashboardViewEnabled,
} from 'src/hooks/product-flags';
import {
  USE_JD_GENERATOR,
  USE_PRESCREENING_GENERATOR,
  USE_RESUME_GENERATOR,
} from 'src/types/roles';

import {MANAGE_CALENDAR} from 'src/action-creators/roles';


export const useExtensionProductsList = (): {
  productsList: Array<string>;
} => {
  // messaging
  const canUseMessaging = useSelector(canUseMsgPhoneOrLatInbox);

  // referrals
  const viewReferrals = usePrivilege('VIEW_REFERRALS_V2');
  const editReferrals = usePrivilege('EDIT_REFERRALS_V2');
  const adminReferrals = usePrivilege('ADMIN_REFERRALS_V2');
  const canViewReferrals = viewReferrals || editReferrals || adminReferrals;
  const referralV2 = useAgencyConfig('enableReferralV2');
  const viewJobsPrivilege = usePrivilege('VIEW_JOBS');
  const adminJobsPrivilege = usePrivilege('ADMIN_JOBS');

  // sense ai
  const hasJDGenerator = usePrivilege(USE_JD_GENERATOR);
  const hasPrescreeningGenerator = usePrivilege(USE_PRESCREENING_GENERATOR);
  const hasResumeGenerator = usePrivilege(USE_RESUME_GENERATOR);

  //meetings
  const schedulerEnabled = useSchedulerEnabled();
  const manageCalendar = usePrivilege(MANAGE_CALENDAR);

  const canUseRefferals = !!referralV2 && canViewReferrals;
  const canUseSenseAI =
    hasJDGenerator || hasPrescreeningGenerator || hasResumeGenerator;
  const isNewAppShellEnabled = useAppShellEnabled();
  const useHVBroadcast = useReleaseFlag('messagingBroadcastV2');
  const dashboardPage = useCandidateDashboardViewEnabled();
  const disableSetOfMessagingProductsForGiGroup = useDisableNewMessageCompose();

  // Micro apps order - Messaging(MSG, HVB, LAT, contacts), Meetings, Jobs, SenseAI, Referrals

  const productsList: Array<string> = [];
  if (canUseMessaging) {
    productsList.push('messaging');
    if (useHVBroadcast) {
      productsList.push('broadcast');
    }

    //(note:) This agency config is used to check if the gi group product requirements.
    // Adding it here because gi group doesn't want the below products along with messaging inside their iframe
    if (!disableSetOfMessagingProductsForGiGroup) {
      if (isNewAppShellEnabled) {
        productsList.push('liveAgentTransfer');
      }
      productsList.push('contacts');
    }
  }

  if (schedulerEnabled && manageCalendar) {
    productsList.push('meetings');
  }

  if (viewJobsPrivilege || adminJobsPrivilege) {
    productsList.push('jobs');
  }

  if (canUseSenseAI) {
    productsList.push('senseai');
  }

  if (canUseRefferals) {
    productsList.push('referral');
  }

  if (dashboardPage) {
    productsList.push('voiceflow');
  }

  return {productsList};
};
